import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { flowRight } from 'lodash';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  appLinkReturnAction,
  fetchAssessmentsByRemediationAssignmentIdAction,
  fetchUsersByIdsAction,
  trackAdobeAction,
  trackNewRelicAction,
} from '../../components/app/app-state-actions.utilities';
import StudentProgressOverview from './StudentProgressOverview.component';
import { UserDto } from '../../apis/eols-user-management-service/eols-user-management-service.dtos';
import withRemediationBase from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import RemediationHeader from '../../components/remediation-header/RemediationHeader.component';
import {
  getReturnLinkId
} from '../../utilities/app.utilities';
import {
  ELSTab,
  ELSTabGroup
} from '../../components/els.components';
import ActivityLog from './ActivityLog.component';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import ActivityProgressSection from './ActivityProgressSection.component';
import IsRender from '../../components/is-render/IsRender.component';
import {
  getActiveAssignmentActivityTypeSettings,
  isInstructorUser,
} from '../../utilities/remediation.utilities';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import {
  RaasAssessmentDto,
  RecommendationGenerationStatusDto,
  RemediationActivityTypeDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import RaasQuizProgressSection from './RaasQuizProgressSection.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { RecordEmpty } from '../../models/common.models';
import { StatusMessage } from '../../components/status-message/StatusMessage.component';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';

export type RemediationStudentActivityState = {
  activeTabIndex: number;
  selectedGoalId: string;
  raasAssessmentDtos: RaasAssessmentDto[];
  usersByIdDto: Record<string, UserDto>;
}

const defaultState: RemediationStudentActivityState = {
  activeTabIndex: 0,
  selectedGoalId: null,
  raasAssessmentDtos: null,
  usersByIdDto: null,
};

export const RemediationStudentActivityComponent = (): JSX.Element => {
  const [state, setState] = useState<RemediationStudentActivityState>(defaultState);

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const mergeState = (newState: Partial<RemediationStudentActivityState>) => {
    setState((prevState) => {
      return {
        ...prevState,
        ...newState
      };
    });
  };

  useEffect(() => {
    if (appState.remediationBaseState?.remediationAssignmentWithSources?.remediationAssignment && !state.raasAssessmentDtos) {
      fetchAssessmentsByRemediationAssignmentIdAction({
        appState,
        appStateRef,
        setAppState,
        remediationAssignmentId: appState.remediationBaseState.remediationAssignmentWithSources.remediationAssignment.id
      })
        .then((assessments) => {
          const userIds = Array.from(new Set(assessments.map((assessment) => assessment.userId)));

          fetchUsersByIdsAction({
            appState,
            appStateRef,
            setAppState,
            ids: userIds
          })
            .then((users) => {
              mergeState({
                usersByIdDto: users,
                raasAssessmentDtos: assessments,
              });
              setAppState((prevState) => {
                return {
                  ...prevState,
                  selectedRaasAssessmentDto: appState.selectedRaasAssessmentDto ? appState.selectedRaasAssessmentDto : appState.remediationBaseState.raasAssessment
                };
              });
            })
            .catch(() => {
              mergeState({
                usersByIdDto: null,
                raasAssessmentDtos: assessments
              });
            });
        })
        .catch(() => {
          mergeState({
            raasAssessmentDtos: null,
            usersByIdDto: null
          });
        })
        .catch(() => {
          mergeState({
            usersByIdDto: null,
            raasAssessmentDtos: null,
          });
        });
    }

  }, [appState.remediationBaseState?.remediationAssignmentWithSources?.remediationAssignment]);

  if (!appState.remediationBaseState?.eolsAssessment && !appState.remediationBaseState?.showPendingAssessmentMessage) {
    // Prevents showing incorrect messaging before any API calls have been complete
    return null;
  }

  const handleReturnNavigation = () => {
    appLinkReturnAction({
      setAppState,
      appState,
      appStateRef,
      linkId: getReturnLinkId(appState.appLinkData, appState.appLinkCookies),
      returnPostBody: null
    });
  };

  const handleSelectedGoal = (goal: AssessmentGoalDto) => {
    mergeState({
      selectedGoalId: goal.vtwId,
      activeTabIndex: 1
    });
  };

  const handleResetSelectedGoal = () => {
    mergeState({
      selectedGoalId: null,
    });
  };

  const handleTabChange = (index: number) => {
    const tabNames = ['studentProgress', 'activityLog'];
    const targetTab = tabNames[index] || 'unknown';

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.TAB_SWITCH}:${targetTab}`,
        }
      }
    });

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.TAB_SWITCHED,
        props: {
          targetTab,
          studentUserId: appState.selectedRaasAssessmentDto?.userId,
          studentStudyEolsAssessmentId: appState.selectedRaasAssessmentDto?.studentStudyEolsAssessmentId,
          remediationAssignmentId: appState.remediationBaseState.remediationAssignmentWithSources?.remediationAssignment?.id
        }
      }
    });

    mergeState({
      activeTabIndex: index
    });
  };

  const handleSelectAssessment = (assessmentId: number) => {
    const remediationAssessment = state.raasAssessmentDtos
      .find((assessment) => assessment.id === assessmentId);

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.INSTRUCTOR_SWITCHED_STUDENT,
        props: {
          studentUserId: remediationAssessment.userId,
          studentStudyEolsAssessmentId: remediationAssessment.studentStudyEolsAssessmentId,
          remediationAssignmentId: remediationAssessment.remediationAssignmentId
        }
      }
    });

    mergeState({
      activeTabIndex: 0
    });
    setAppState((prevState) => {
      return {
        ...prevState,
        selectedRaasAssessmentDto: remediationAssessment
      };
    });
  };

  const isRelatedToStudent = (): boolean => {
    const { raasAssessment } = appState.remediationBaseState;
    return raasAssessment && (raasAssessment.courseSectionId === Number(appState.courseSectionId));
  };

  const isAssessmentGenerationCompleted = (): boolean => {
    const { raasAssessment } = appState.remediationBaseState;
    return raasAssessment && raasAssessment.recGenerationStatus === RecommendationGenerationStatusDto.COMPLETED;
  };

  if (!isInstructorUser(appState.userRole)) {
    return null;
  }

  return (
    <>
      <RemediationHeader
        handleReturnNavigation={handleReturnNavigation}
        showSherpathAIChatToggle={false}
      />
      <div className="c-nra-remediation-student-activity__main">
        <div className="c-nra-remediation-student-activity__center">
          {(isRelatedToStudent() && isAssessmentGenerationCompleted())
            && (
              <>
                <StudentProgressOverview
                  selectedAssessmentDto={appState.selectedRaasAssessmentDto}
                  usersByIdDto={state.usersByIdDto}
                  raasAssessmentDtos={state.raasAssessmentDtos}
                  handleSelectAssessment={handleSelectAssessment}
                />
                <IsRender isRender={!!state.raasAssessmentDtos && state.raasAssessmentDtos.length > 0}>
                  <div>
                    <ELSTabGroup
                      onChangeActiveIndex={handleTabChange}
                      activeIndex={state.activeTabIndex}
                    >
                      <ELSTab title="Student Progress">
                        <>
                          {
                            getActiveAssignmentActivityTypeSettings(appState.remediationBaseState)
                              .map((activityType, idx, array) => {
                                const isLastItem = idx === array.length - 1;
                                return (
                                  <div key={activityType.activityType}>
                                    {
                                      activityType.activityType !== RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE ? (
                                        <ActivityProgressSection
                                          activityType={activityType.activityType}
                                          handleSelectGoal={handleSelectedGoal}
                                        />
                                      ) : (
                                        <RaasQuizProgressSection />
                                      )
                                    }
                                    {
                                      array.length > 1 && !isLastItem && (
                                        <div className="c-els-divider--1x u-els-color-n7 u-els-margin-top-3x u-els-margin-bottom-1x" />
                                      )
                                    }
                                  </div>
                                );
                              })
                          }
                        </>
                      </ELSTab>
                      <ELSTab title="Activity Log">
                        <ActivityLog
                          selectedGoalId={state.selectedGoalId}
                          handleResetSelectedGoal={handleResetSelectedGoal}
                        />
                      </ELSTab>
                    </ELSTabGroup>
                  </div>
                </IsRender>
              </>
            )
          }
          {!isRelatedToStudent()
            && (
              <div className="c-nra-remediation-home">
                <FlexLayout modifiers={[FlexLayoutModifier.CENTER]}>
                  <FlexItem>
                    <div className="c-nra-remediation-home__main u-els-margin-top-3x">
                      <div className="c-nra-remediation-home__center">
                        <StatusMessage raasAssessment={null} />
                      </div>
                    </div>
                  </FlexItem>
                </FlexLayout>
              </div>
            )
          }
          {
            appState.remediationBaseState.showPendingAssessmentMessage
            && appState.remediationBaseState.raasAssessment
            && (
              <div className="c-nra-remediation-home">
                <FlexLayout modifiers={[FlexLayoutModifier.CENTER]}>
                  <FlexItem>
                    <div className="c-nra-remediation-home__main u-els-margin-top-3x">
                      <div className="c-nra-remediation-home__center">
                        <StatusMessage raasAssessment={appState.remediationBaseState.raasAssessment} />
                      </div>
                    </div>
                  </FlexItem>
                </FlexLayout>
              </div>
            )
          }
        </div>
      </div>
    </>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Remediation' }),
  withRemediationBase(),
  withPageLoader,
);

const RemediationStudentActivity = enhance(RemediationStudentActivityComponent) as React.FC<RecordEmpty>;

export default RemediationStudentActivity;
